import { Link } from '@tbitop/mobile-app';
import type { InternalUrl } from '@tbitop/routing';
import { ButtonCtaDanger, ButtonCtaPrimary, Card, ErgebnisOptionenModal, Header4, Paragraph, ShortDate, TagNeutral } from '@tbitop/ui';
import { CalendarBlank, CheckCircle, XCircle } from 'phosphor-react';
import type { ComponentProps } from 'react';
import { useId, useState } from 'react';
import type { AufgabeErgebnis } from '../../../../infrastructure/graphql';
import { useAufgabe, useAufgabeAbschliessen } from '../../../../infrastructure/graphql';
import type { AufgabeQuery } from '../../../../infrastructure/graphql/__generated__/aufgabe';

type AufgabenListItemProps = {
  aufgabe: {
    id: string;
    typ: string;
    titel: string;
    faelligkeit: string | null;
    kampagne?: string | null;
  };
  href: string | InternalUrl;
  showAbschliessenButtons?: boolean;
};

export const AufgabenListItem = ({ aufgabe, href, showAbschliessenButtons }: AufgabenListItemProps) => {
  const headerId = useId();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ergebnis, setErgebnis] = useState<AufgabeErgebnis | null>(null);
  const { aufgabe: aufgabenDetails, loading: loadingAufgabenDetails } = useAufgabe(aufgabe.id, !isModalOpen || !showAbschliessenButtons);
  const { aufgabeAbschliessen } = useAufgabeAbschliessen();

  if (!aufgabe) {
    return null;
  }

  function onAbschliessenClick(e: React.MouseEvent, type: 'Erfolgreich' | 'Erfolglos') {
    e.preventDefault();
    setIsModalOpen(true);
    setErgebnis(type);
  }

  function closeModal() {
    setIsModalOpen(false);
    setErgebnis(null);
  }

  const handleErgebnisErfasst = async (ergebnis: AufgabeErgebnis | null, ...[ergebnisOption, kommentar]: Parameters<ComponentProps<typeof ErgebnisOptionenModal>['onErgebnisErfasst']>) => {
    if (ergebnis === 'Erfolgreich' || ergebnis === 'Erfolglos') {
      await aufgabeAbschliessen(aufgabe.id, ergebnis, ergebnisOption, kommentar);
    }
    closeModal();
  };

  return (
    <li aria-labelledby={headerId}>
      <Link href={href}>
        <Card displayAsClickable className="flex flex-col gap-200">
          <TagNeutral text={aufgabe.typ} className="self-start"/>
          <Header4 id={headerId}>{aufgabe.titel}</Header4>
          {aufgabe.kampagne && <Paragraph>{aufgabe.kampagne}</Paragraph>}
          {aufgabe.faelligkeit ? <DueDate date={aufgabe.faelligkeit} /> : null}
          {showAbschliessenButtons &&
          <div className="flex justify-end gap-3">
            <ButtonCtaPrimary hideLabel className="bg-success border-success" icon={CheckCircle} onClick={e => onAbschliessenClick(e, 'Erfolgreich')}/>
            <ButtonCtaDanger hideLabel icon={XCircle} onClick={e => onAbschliessenClick(e, 'Erfolglos')}/>
          </div>}
        </Card>
      </Link>
      {isModalOpen && !loadingAufgabenDetails && aufgabenDetails ?
        <ErgebnisOptionenModal
          aufgabe={aufgabenDetails as AufgabeQuery['aufgabe']}
          ergebnis={ergebnis}
          onClose={closeModal}
          onErgebnisErfasst={(ergebnisOption, kommentar) => handleErgebnisErfasst(ergebnis, ergebnisOption, kommentar)} /> : null}
    </li>
  );
};

type DueDateProps = {
  date: string;
};

const DueDate = ({ date }: DueDateProps) => (
  <Paragraph className="flex items-center text-tds-color-copy-on-light-50 gap-50">
    <CalendarBlank size={14} />
    <ShortDate date={date} />
  </Paragraph>
);
